// Epoch 19: Lead Generator Enhancements - Filter Sidebar Component
// File: src/components/filters/FilterSidebar.tsx
// Purpose: Main Apollo-style sidebar with responsive multi-select filtering

import React, { useState } from 'react';
import { FilterSidebarProps } from '../../types/filters';
import FilterCategory from './FilterCategory';
import TierSelectionCategory from './TierSelectionCategory';
import AppliedFiltersDisplay from './AppliedFiltersDisplay';
import FilterSummary from './FilterSummary';

export default function FilterSidebar({
  filters,
  metadata,
  onToggleOption,
  onClearFilter,
  onClearAll,
  isCollapsed = false,
  onToggleCollapse,
  className = ''
}: Omit<FilterSidebarProps, 'onFilterChange'>) {
  const [searchTerms, setSearchTerms] = useState<Record<string, string>>({});

  // Filter category configuration
  const filterCategories = [
    {
      key: 'countries' as const,
      label: 'Country',
      searchable: true,
      defaultExpanded: true
    },
    {
      key: 'cities' as const,
      label: 'City',
      searchable: true,
      defaultExpanded: true
    },
    {
      key: 'jobLevels' as const,
      label: 'Job Level',
      searchable: false,
      defaultExpanded: true
    },
    {
      key: 'jobFunctions' as const,
      label: 'Job Function',
      searchable: false,
      defaultExpanded: true
    },
    {
      key: 'industries' as const,
      label: 'Industry',
      searchable: true,
      defaultExpanded: true
    }
  ];

  // Collapsed sidebar view
  if (isCollapsed) {
    return (
      <div className={`w-12 bg-white border-r border-gray-200 flex flex-col h-full ${className}`}>
        <div className="flex-1">
          <button
            onClick={onToggleCollapse}
            className="w-full h-12 flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-50 transition-colors"
            aria-label="Expand filters"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
        
        {/* Collapsed filter indicator */}
        {(filters.countries.length > 0 || filters.cities.length > 0 || filters.jobLevels.length > 0 || 
          filters.jobFunctions.length > 0 || filters.industries.length > 0 || filters.tiers.length < 5) && (
          <div className="w-3 h-3 bg-blue-500 rounded-full mx-auto mb-4"></div>
        )}
      </div>
    );
  }

  return (
    <div className={`w-80 bg-white border-r border-gray-200 flex flex-col h-full ${className}`}>
      {/* Sidebar Header */}
      <div className="sticky top-0 bg-white border-b border-gray-200 p-4 z-10">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
          {onToggleCollapse && (
            <button
              onClick={onToggleCollapse}
              className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
              aria-label="Collapse filters"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}
        </div>
        
        {/* Filter Summary */}
        <div className="mt-3">
          <FilterSummary filters={filters} onClearAll={onClearAll} />
        </div>
      </div>

      {/* Applied Filters Display */}
      <AppliedFiltersDisplay 
        filters={filters} 
        metadata={metadata}
        onRemoveFilter={onToggleOption}
      />

      {/* Filter Categories - Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-4 space-y-6">
          {filterCategories.map((category) => (
            <FilterCategory
              key={category.key}
              category={category}
              selectedValues={filters[category.key] as string[]}
              options={metadata?.[category.key] || []}
              onToggleOption={(value) => onToggleOption(category.key, value)}
              onClearCategory={() => onClearFilter(category.key)}
              searchTerm={searchTerms[category.key] || ''}
              onSearchChange={(term) => setSearchTerms(prev => ({ ...prev, [category.key]: term }))}
            />
          ))}
          
          {/* Tier Selection - Special Component */}
          <TierSelectionCategory
            selectedTiers={filters.tiers}
            onToggleTier={(tier) => onToggleOption('tiers', tier)}
            onClearTiers={() => onClearFilter('tiers')}
          />
        </div>
      </div>

      {/* Footer with loading state */}
      {!metadata && (
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-center space-x-2 text-sm text-gray-500">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
            <span>Loading filter options...</span>
          </div>
        </div>
      )}
    </div>
  );
}